// useSelectOnFocus — select a single-line text input's contents on focus, so
// typing REPLACES the value instead of appending (browser-URL-bar / spreadsheet /
// MUI `selectOnFocus` behavior). ONE canonical implementation, shared by the text
// primitives (Input, NumberInput) so the behavior never drifts.
//
// The guarded `onMouseUp` is the non-obvious, load-bearing half: after a click the
// event order is mousedown → focus → mouseup, and that trailing mouseup collapses
// the fresh selection back to a caret. We preventDefault ONLY the mouseup that
// accompanied the focusing click (tracked by `armed`), so the selection survives
// the click but a SECOND click (already focused → onFocus doesn't refire) still
// positions the caret normally — you can click-to-reposition, you just can't on the
// very first click.
//
// Select fires on focus ENTERING the field, never on a re-focus while the caret is
// already inside (tracked by `inside`, cleared on blur). A focus event is NOT proof
// of entry: React Aria re-focuses its own input once its overlay mounts, firing a
// second focus with no blur between — and re-selecting there swallows the character
// the user just typed (type `a`, watch it select itself, next keystroke replaces it).
// Consumers must route their own onBlur through this hook so `inside` can clear.
//
// Multi-line editing (Textarea) deliberately does NOT use this — select-all-on-focus
// is hostile there (click to edit one word, lose the paragraph).

import { useRef, type FocusEvent, type MouseEvent } from 'react';

export interface SelectOnFocusHandlers {
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onMouseUp?: (e: MouseEvent<HTMLInputElement>) => void;
}

/**
 * Returns `{ onFocus, onBlur, onMouseUp }` to spread on an `<input>`. Pass the
 * consumer's own handlers to compose them (they run after ours). When `enabled` is
 * false the passed-in handlers are returned unchanged (behavior opt-out).
 */
export function useSelectOnFocus(enabled: boolean, handlers: SelectOnFocusHandlers = {}): SelectOnFocusHandlers {
  const armed = useRef(false);
  const inside = useRef(false);
  const { onFocus, onBlur, onMouseUp } = handlers;
  if (!enabled) return handlers;
  return {
    onFocus: (e) => {
      // Entry only — a re-focus while the caret is already inside is React Aria's
      // overlay handing focus back, not the user arriving.
      if (!inside.current) {
        e.currentTarget.select();
        armed.current = true;
      }
      inside.current = true;
      onFocus?.(e);
    },
    onBlur: (e) => {
      // Focus bouncing into the field's OWN popup is not the caret leaving it.
      // React Aria walks focus onto an option row when its listbox opens and
      // hands it straight back, so a naive blur would re-arm the select and eat
      // the character just typed. `aria-controls` names that popup — a plain
      // input has none, so the check no-ops there.
      const controlsId = e.currentTarget.getAttribute('aria-controls');
      const popup = controlsId ? document.getElementById(controlsId) : null;
      const movingTo = e.relatedTarget as Node | null;
      if (!(popup && movingTo && popup.contains(movingTo))) {
        inside.current = false;
        armed.current = false;
      }
      onBlur?.(e);
    },
    onMouseUp: (e) => {
      if (armed.current) {
        e.preventDefault();
        armed.current = false;
      }
      onMouseUp?.(e);
    },
  };
}
